import React, { useRef, useState } from 'react'
import {
  CAlert,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CRow,
  CLoadingButton,
  CFormInput,
  CFormSelect,
  CFormLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CMultiSelect,
  CFormTextarea,
  CButton,
  CTooltip,
} from '@coreui/react-pro'
import Bootbox from 'bootbox-react'
import moment from 'moment'
import { cilArrowLeft, cilInfo, cilLink, cilList, cilStar } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import { useFormik } from 'formik'
import ToastComponent from 'src/components/common/TaostComponent'
import ScorecardService from 'src/service/ScorecardService'
import { Link, useNavigate } from 'react-router-dom'
import Loader from 'src/components/Loader'
import ScorecardGrid from './ScorecardGrid'
import { hasScores } from './scorecardConfig'
import './ScoreCard.css'
import MarkAsCompleteModal from './MarkAsCompleteModal'
import LegendInfo from './LegendInfo'

// The borderless-alert InfoBox the import wizards use, copied rather than shared -
// same house convention as MiniStepper/StatTile.
const InfoBox = ({ children, color = 'info' }) => (
  <CAlert color={color} className="d-flex align-items-start gap-2 border-0 mb-3">
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

const TeamCommonScorecard = (props) => {
  const navigate = useNavigate()
  const formRef = useRef(null)
  const [detailsModalVisible, setDetailsModalVisible] = useState(false)
  const [bonusModalVisible, setBonusModalVisible] = useState(false)
  const formik = useFormik({
    initialValues: {
      data: props.scorecardDetails,
      scorcard_link_name: props.fixtureDetails?.scorcard_link_name,
      scorcard_link_url: props.fixtureDetails?.scorcard_link_url,
      fall_of_wickets: props.fixtureDetails?.fall_of_wickets,
      match_report: props.fixtureDetails?.match_report,
      potm_match_status: props.fixtureDetails?.potm_match_status ?? 0,
      potm_match_status2: props.fixtureDetails?.potm_match_status2 ?? 0,
      potm: props.fixtureDetails?.potm ?? [], // for Team 1
      potm2: props.fixtureDetails?.potm2 ?? [], // for Team 2
      match_status: '',
    },
    enableReinitialize: true,
    onSubmit: (data, actions) => {
      props.setCommonScorecardLoading(true)
      data.fixtureId = props.fixtureId
      // data.potm = props.selectedValue
      data.status = props.statusValue
      data.inning = props.defaultInning
      data.paramTeamId = props.teamId
      props.setLoader(true)
      ScorecardService.saveScorecard(data).then((res) => {
        if (res.status === 200) {
          props.setCommonScorecardLoading(false)
          props.fetchData(props.defaultInning)
          ToastComponent(res.message, 'success')
        }
      })
    },
  })

  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const handleResetConfirm = () => {
    resetFixtureScorecard()
    return setShowResetConfirm(false)
  }
  const handleResetCancel = () => {
    return setShowResetConfirm(false)
  }
  const clearScorecard = () => {
    setShowResetConfirm(true)
    console.log('clear scorecard')
  }
  const resetFixtureScorecard = () => {
    const data = {}
    data.fixtureId = props.fixtureId
    data.inning = props.defaultInning
    ScorecardService.clearFixtureScorecard(data).then((res) => {
      if (res.status === 200) {
        props.setCommonScorecardLoading(false)
        props.fetchData()
        ToastComponent(res.message, 'success')
      }
    })
  }

  const handleOutsideButtonClick = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(new Event('submit')) // Trigger form submission
    }
  }
  // ---- presentation helpers ------------------------------------------------

  // Same team-name resolution the old header did inline: single-team games show
  // the fixture's team, two-team games show whichever side this scorecard is for.
  const teamName =
    props.gameDetails?.game_type == 1
      ? props.fixtureDetails?.team_name
      : (props.fixtureDetails?.team == props.teamId && props.fixtureDetails?.team_name) ||
        (props.fixtureDetails?.team2 == props.teamId && props.fixtureDetails?.team2_name)

  // Both scorecard-detail fields autosave through updateCustomScorecardField on
  // change. Formik is kept in step so the Save scores payload carries what is on
  // screen - save-scorecard writes these two columns too, and would otherwise put
  // the pre-edit value back.
  const handleScorecardDetailChange = (e, type) => {
    formik.handleChange(e)
    props.handleInputChange(e, type)
  }

  // Marker on the header buttons, so a panel that already holds something reads as
  // filled without being opened.
  const scorecardDetailsSet = Boolean(
    formik.values.scorcard_link_name || formik.values.scorcard_link_url,
  )
  const matchResultSet = (value) => ['1', '2'].includes(String(value ?? ''))
  const bonusPointsSet =
    (formik.values.potm?.length ?? 0) > 0 ||
    (formik.values.potm2?.length ?? 0) > 0 ||
    matchResultSet(formik.values.potm_match_status) ||
    matchResultSet(formik.values.potm_match_status2)

  const enteredCount = (props.scorecardDetails || []).filter((item) =>
    hasScores(item, props.scorecardColoumns),
  ).length
  const playerCount = (props.scorecardDetails || []).length
  const enteredPercent = playerCount ? Math.round((enteredCount / playerCount) * 100) : 0

  const scorecardDetailsModal = (
    <CModal
      visible={detailsModalVisible}
      onClose={() => setDetailsModalVisible(false)}
      aria-labelledby="scorecard-details-modal-title"
    >
      <CModalHeader>
        <CModalTitle id="scorecard-details-modal-title">Scorecard Details</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <InfoBox>
          Where this scorecard came from. Both fields save as you type &mdash; there is no separate
          save step here.
        </InfoBox>
        <CRow className="g-3">
          <CCol xs={12}>
            <CFormLabel htmlFor="scorcard_link_name">Scorecard Source</CFormLabel>
            <CFormInput
              type="text"
              name="scorcard_link_name"
              id="scorcard_link_name"
              placeholder="Scorecard Source"
              value={formik.values.scorcard_link_name ?? ''}
              onChange={(e) => handleScorecardDetailChange(e, 'scorcard_link_name')}
            />
            <div className="form-text">Who published it &mdash; PlayHQ, MyCricket, the club.</div>
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="scorcard_link_url">Scorecard Link</CFormLabel>
            <CFormInput
              type="text"
              name="scorcard_link_url"
              id="scorcard_link_url"
              placeholder="Scorecard Link"
              value={formik.values.scorcard_link_url ?? ''}
              onChange={(e) => handleScorecardDetailChange(e, 'scorcard_link_url')}
            />
            <div className="form-text">
              Added here, the Player stats heading becomes a link to the published scorecard.
            </div>
          </CCol>
          {/* <CCol xs={12}>
            <CFormLabel htmlFor="fall_of_wickets">Fall of Wickets</CFormLabel>
            <CFormTextarea
              aria-label="With textarea"
              defaultValue={formik.values.fall_of_wickets}
              name={'fall_of_wickets'}
              onChange={(e) => props.handleInputChange(e, 'fall_of_wickets')}
            ></CFormTextarea>
          </CCol> */}
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="primary" onClick={() => setDetailsModalVisible(false)}>
          Done
        </CButton>
      </CModalFooter>
    </CModal>
  )

  const bonusPointsModal = (
    <CModal
      className="sc-form-modal"
      visible={bonusModalVisible}
      onClose={() => setBonusModalVisible(false)}
      aria-labelledby="bonus-points-modal-title"
    >
      <CModalHeader>
        <CModalTitle id="bonus-points-modal-title">Bonus Points</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <InfoBox>
          Points awarded on top of the stats above. These are saved with the scores &mdash; press
          <strong> Save scores</strong> on the scorecard once you are done here.
        </InfoBox>
        <CRow className="g-3">
          <CCol xs={12}>
            <CFormLabel>Player Of The Match</CFormLabel>
            {/* Team 1 POTM */}
            {props.fixtureDetails?.team == props.teamId && props.fixtureDetails?.team_name && (
              <>
                <label>&nbsp;for {props.fixtureDetails?.team_name}</label>
                <CMultiSelect
                  id="potm"
                  name="potm"
                  options={props.playerList}
                  selectionType="tags"
                  optionsMaxHeight={220}
                  onChange={(selected) => {
                    formik.setFieldValue(
                      'potm',
                      selected.map((s) => s.value),
                    )
                  }}
                  value={formik.values.potm
                    ?.map((val) => props.playerList.find((obj) => obj.value === val))
                    .filter(Boolean)}
                />
              </>
            )}

            {/* Team 2 POTM */}
            {props.fixtureDetails?.team2 == props.teamId && props.fixtureDetails?.team2_name && (
              <>
                <label>&nbsp;for {props.fixtureDetails?.team2_name}</label>
                <CMultiSelect
                  id="potm2"
                  name="potm2"
                  options={props.playerList.map((opt) => ({
                    ...opt,
                    selected: (formik.values.potm2 || []).includes(opt.value),
                  }))}
                  selectionType="tags"
                  optionsMaxHeight={220}
                  onChange={(selected) => {
                    formik.setFieldValue(
                      'potm2',
                      selected.map((s) => s.value),
                    )
                  }}
                />
              </>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel>Match Result</CFormLabel>
            {/* Team 1 */}
            {props.fixtureDetails?.team == props.teamId && props.fixtureDetails?.team_name && (
              <>
                <label>&nbsp;for {props.fixtureDetails?.team_name}</label>
                <CFormSelect
                  id="potm_match_status"
                  name="potm_match_status"
                  value={formik.values.potm_match_status}
                  onChange={formik.handleChange}
                >
                  <option>Select Match Result</option>
                  <option value="1">Won</option>
                  <option value="2">Other</option>
                </CFormSelect>
              </>
            )}

            {/* Team 2 */}
            {props.fixtureDetails?.team2 == props.teamId && props.fixtureDetails?.team2_name && (
              <>
                <label>&nbsp;for {props.fixtureDetails?.team2_name}</label>
                <CFormSelect
                  id="potm_match_status2"
                  name="potm_match_status2"
                  value={formik.values.potm_match_status2}
                  onChange={formik.handleChange}
                >
                  <option>Select Match Result</option>
                  <option value="1">Won</option>
                  <option value="2">Other</option>
                </CFormSelect>
              </>
            )}
          </CCol>
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="primary" onClick={() => setBonusModalVisible(false)}>
          Done
        </CButton>
      </CModalFooter>
    </CModal>
  )

  return props?.commonScorecardLoading ? (
    <Loader />
  ) : (
    <>
      <CForm onSubmit={formik.handleSubmit}>
        <div className="ge-page-header">
          <button type="button" className="ge-back-link" onClick={() => navigate(-1)}>
            <CIcon icon={cilArrowLeft} />
            Manage Scores
          </button>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
            <div>
              <h1 className="ge-page-title">Scorecard &mdash; {teamName}</h1>
              <p className="ge-page-subtitle">
                Enter each player&rsquo;s stats for this innings. Fantasy points are recalculated
                when you save.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <CButton
                color="secondary"
                variant="outline"
                href={`/manage-scores/team-players/${props.fixtureId}/${
                  props.teamId ?? props.param?.teamId
                }`}
              >
                <CIcon icon={cilList} className="me-1" />
                Squad
              </CButton>
              <CButton color="secondary" variant="outline" onClick={props.handleInfoClick}>
                <CIcon icon={cilInfo} className="me-1" />
                Column legend
              </CButton>
            </div>
          </div>
        </div>

        <div className="ge-meta-strip">
          <div className="ge-meta-item">
            <span className="ge-meta-label">Round</span>
            <span className="ge-meta-value">{props.fixtureDetails.round}</span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Team</span>
            <span className="ge-meta-value">{teamName}</span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Starts</span>
            <span className="ge-meta-value">
              {moment(props.fixtureDetails.start_date).format('D MMM YYYY')}
              {', '}
              {moment(props.fixtureDetails.start_time, ['HH:mm']).format('h:mma')}
            </span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Ends</span>
            <span className="ge-meta-value">
              {moment(props.fixtureDetails.end_date).format('D MMM YYYY')}
              {', '}
              {moment(props.fixtureDetails.end_time, ['HH:mm']).format('h:mma')}
            </span>
          </div>
        </div>

        <div className="sc-toolbar">
          <div className="d-flex flex-wrap align-items-center gap-3">
            {props.fixtureDetails?.match_type_name === '2-Day' && (
              <div className="d-flex align-items-center gap-2">
                <CFormLabel
                  htmlFor="inning"
                  className="mb-0 small fw-semibold text-medium-emphasis"
                >
                  Innings
                </CFormLabel>
                <CFormSelect
                  aria-label="select inning"
                  name="inning"
                  onChange={props.changeInning}
                  value={props.defaultInning}
                  id="inning"
                  style={{ width: 150 }}
                >
                  <option value={1}>1st Innings</option>
                  <option value={2}>2nd Innings</option>
                </CFormSelect>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="small fw-medium text-medium-emphasis">
              {enteredCount} of {playerCount} players have scores saved
            </span>
            <div className="sc-progress-track">
              <div className="sc-progress-fill" style={{ width: `${enteredPercent}%` }} />
            </div>
          </div>
        </div>

        <CCard className="mb-4">
          <CCardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <strong>
              {props.fixtureDetails.scorcard_link_url ? (
                <Link to={props.fixtureDetails.scorcard_link_url} target="_blank">
                  Player stats
                </Link>
              ) : (
                'Player stats'
              )}
            </strong>
            <div className="sc-header-actions">
              <CTooltip content="Scorecard Details">
                <CButton
                  color="secondary"
                  variant="ghost"
                  className="sc-icon-btn"
                  onClick={() => setDetailsModalVisible(true)}
                  aria-label="Scorecard Details"
                >
                  <CIcon icon={cilLink} size="lg" />
                  {scorecardDetailsSet && <span className="sc-icon-btn-dot" aria-hidden="true" />}
                </CButton>
              </CTooltip>
              <CTooltip content="Bonus Points">
                <CButton
                  color="secondary"
                  variant="ghost"
                  className="sc-icon-btn"
                  onClick={() => setBonusModalVisible(true)}
                  aria-label="Bonus Points"
                >
                  <CIcon icon={cilStar} size="lg" />
                  {bonusPointsSet && <span className="sc-icon-btn-dot" aria-hidden="true" />}
                </CButton>
              </CTooltip>
            </div>
          </CCardHeader>
          <CCardBody>
            {props.loader || props.isLoading ? (
              <Loader />
            ) : (
              <ScorecardGrid
                columns={props.scorecardColoumns}
                rows={props.scorecardDetails}
                isMobile={props.isMobile}
                onChange={formik.handleChange}
                formatFantasyPoints={props.formatFantasyPoints}
              />
            )}
          </CCardBody>
        </CCard>

        <div className="sc-savebar sc-savebar--pinned">
          <span className="small text-medium-emphasis">
            {enteredCount} of {playerCount} players have scores saved. Saving recalculates fantasy
            points for this innings.
          </span>
          <CLoadingButton type="submit" color="primary" loading={props.loader}>
            Save scores
          </CLoadingButton>
        </div>
      </CForm>

      {scorecardDetailsModal}
      {bonusPointsModal}

      <Bootbox
        show={showResetConfirm}
        type={'confirm'}
        message={'Are you sure you want to reset this?'}
        onSuccess={handleResetConfirm}
        onCancel={handleResetCancel}
        onClose={handleResetCancel}
      />
    </>
  )
}

export default TeamCommonScorecard
